// In-house notification preferences — stored in localStorage

const PREFS_KEY = "moneyx_notification_prefs_v1";
const TRIGGERED_KEY = "moneyx_notif_triggered_v1";

export type PriceAlert = {
  id: string;
  symbol: string;
  targetPrice: number;
  direction: "above" | "below";
  enabled: boolean;
};

export type NotificationPrefs = {
  browserEnabled: boolean;
  liquidationRisk: boolean;
  tradeConfirmations: boolean;
  priceAlerts: PriceAlert[];
};

const DEFAULT_PREFS: NotificationPrefs = {
  browserEnabled: false,
  liquidationRisk: true,
  tradeConfirmations: true,
  priceAlerts: [],
};

export function loadPrefs(): NotificationPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function savePrefs(prefs: NotificationPrefs): void {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
}

export function loadTriggeredAlerts(): Record<string, number> {
  try {
    const raw = localStorage.getItem(TRIGGERED_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveTriggeredAlerts(map: Record<string, number>): void {
  localStorage.setItem(TRIGGERED_KEY, JSON.stringify(map));
}

export function makePriceAlertId(): string {
  return `pa_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

export function browserNotificationState(): "granted" | "denied" | "default" | "unsupported" {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission as "granted" | "denied" | "default";
}

export async function requestBrowserPermission(): Promise<boolean> {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  const result = await Notification.requestPermission();
  return result === "granted";
}

export function fireNotification(title: string, body: string, tag?: string) {
  if (Notification.permission !== "granted") return;
  try {
    new Notification(title, {
      body,
      tag,
      icon: "/favicon.ico",
      badge: "/favicon.ico",
    });
  } catch {
    // Some browsers restrict Notification in non-service-worker context — silently ignore
  }
}
